import {
  adjust,
  append,
  lensProp,
  over,
  any,
  insert,
  Path,
  assocPath,
  dissocPath,
  path,
  last,
  assoc,
  clone,
} from 'ramda';
import {
  getConditionFields,
  IAttributeInt,
  AttributePredicateType,
  AssociationPredicateType,
  getGroupSortOrder,
  getAttributeSortOrder,
  getTypeTree,
} from '../../../../../Model/avenyModelUtils';
import {
  IClientAssociationModel,
  IClientAttributeModel,
  IClientBusinessTypeModel,
} from '../../../../../Types/ClientModelTypes';
import {
  createConditionPath,
  getDefaultNonExtendedConditionObject,
  getDefaultExtendedConditionObject,
  getDefaultConditionObject,
} from './UrvalConditionUtils';
import {
  IUrvalFieldsAssociation,
  IUrvalExtendedField,
  IUrvalField,
  UrvalFieldSortModeType,
  FieldPickerItemType,
} from '../Types/UrvalFieldPickerTypes';
import {
  UIUrvalConditionObjectType,
  ITypeTreeConditionObject,
  IExtendedConditionObject,
  IUIPage,
} from '../../../../../Types/UrvalTypes';
import { AvenyCollectionMap } from '../../../../../Model/ModelContext/ModelContext';
import { stringCompareNumeric } from '../../../../../Utils/mergeSort';
import {
  getSection,
  ITreeSection,
} from '../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import shortid from 'shortid';
import mergeSort from '../../../../../Utils/mergeSort';
import { getTypeConditionObject } from '../../../Utils/urvalUtils';
import { createFieldPickerItems } from './FieldPickerItemsConvertUtils';
import { rutinPredicates } from '../../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/Utils/Predicates.utils';
import { IRutinFieldsPredicate } from '../../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';

export interface IUrvalPickerData {
  type: 'field' | 'association' | 'group' | 'page' | 'extended';
  association?: IClientAssociationModel;
  attribute?: IClientAttributeModel;
  modelPath?: IClientAssociationModel[];
  conditionObject?: IExtendedConditionObject;
  sortOrder?: number;
  typeTree?: IClientBusinessTypeModel;
}

const compareDisplayLabel = (
  a: ITreeSection<IUrvalPickerData>,
  b: ITreeSection<IUrvalPickerData>
): number => stringCompareNumeric(a.name, b.name);

const getSortOrder = (
  field: ITreeSection<IUrvalPickerData>
): number | undefined => {
  let sortOrder: number | undefined;
  if (field.data?.type === 'group' || field.data?.type === 'field') {
    sortOrder = field.data.sortOrder;
  }

  return sortOrder;
};

const compareSortOrder = (
  a: ITreeSection<IUrvalPickerData>,
  b: ITreeSection<IUrvalPickerData>
): number => {
  const aSortOrder = getSortOrder(a);
  const bSortOrder = getSortOrder(b);

  if (aSortOrder != null && bSortOrder != null) {
    return aSortOrder - bSortOrder;
  } else {
    return compareDisplayLabel(a, b);
  }
};

const getModelPath = (
  modelPath?: IClientAssociationModel[],
  integratedIn?: IClientAssociationModel[]
): IClientAssociationModel[] => {
  if (modelPath && integratedIn) {
    return modelPath.concat(integratedIn);
  } else if (modelPath) {
    return modelPath;
  } else if (integratedIn) {
    return integratedIn;
  } else {
    return [];
  }
};

const createPlainField =
  (
    collectionName: string,
    componentName: string | null,
    modelPath: IClientAssociationModel[] | undefined,
    collectionMap: AvenyCollectionMap
  ) =>
  (attr: IAttributeInt): ITreeSection<IUrvalPickerData> => {
    const value = `${createConditionPath(
      attr.attribute.AttributeName,
      getModelPath(modelPath, attr.integratedIn)
    )}`;

    return {
      name: attr.attribute.DisplayLabel,
      value: value,
      id: value + shortid.generate(),
      data: {
        type: 'field',
        attribute: attr.attribute,
        modelPath: getModelPath(modelPath, attr.integratedIn),
        sortOrder: getAttributeSortOrder(attr.attribute.Properties),
        typeTree:
          attr.attribute.InterfaceType === 'ifTypeTree'
            ? getTypeTree(collectionMap, collectionName, componentName)
            : undefined,
      },
    };
  };

const getCompareFunction = (sortMode: UrvalFieldSortModeType) => {
  if (sortMode === 'CustomSortOrder') {
    return compareSortOrder;
  } else {
    return compareDisplayLabel;
  }
};

const fieldsLens = lensProp('subList');

export const getFields = (
  collectionName: string,
  componentName: string | null,
  model: AvenyCollectionMap,
  sortMode: UrvalFieldSortModeType,
  initialOpen: boolean,
  modelPath?: IClientAssociationModel[],
  typeConditionObject?: ITypeTreeConditionObject,
  attributePredicate?: AttributePredicateType,
  associationPredicate?: AssociationPredicateType
): ITreeSection<IUrvalPickerData>[] => {
  attributePredicate =
    attributePredicate == null ? () => true : attributePredicate;
  associationPredicate =
    associationPredicate == null ? () => true : associationPredicate;

  const {
    attributesWithoutGroup,
    attributesWithGroup,
    pageAssociations,
    extendedAssociations,
  } = getConditionFields(
    collectionName,
    componentName,
    attributePredicate,
    associationPredicate,
    modelPath,
    model,
    typeConditionObject
  );

  let plainFields = attributesWithoutGroup.map(
    createPlainField(collectionName, componentName, modelPath, model)
  );

  //Ta bort typvillkor om man är på toppnivån av fält
  if (modelPath == null) {
    plainFields = plainFields.filter(attr => attr.value !== '_typ');
  }

  const pageAssociationFields: ITreeSection<IUrvalPickerData>[] =
    pageAssociations.map(association => {
      const modelPathWithIntegrated = getModelPath(
        modelPath,
        association.integratedIn
      );

      const value = createConditionPath(
        association.association.AssociationName,
        modelPath
      );

      return {
        name: association.association.DisplayLabel,
        value: value,
        id: value + shortid.generate(),
        data: {
          association: association.association,
          modelPath: modelPathWithIntegrated,
          type: 'association' as 'association',
        },
        open: initialOpen,
      };
    });

  const extendedAssociationFields: ITreeSection<IUrvalPickerData>[] =
    extendedAssociations.map(association => {
      const value = createConditionPath(
        association.association.AssociationName,
        modelPath
      );
      return {
        name: association.association.DisplayLabel,
        value: value,
        id: value + shortid.generate(),
        data: {
          association: association.association,
          modelPath: getModelPath(modelPath, association.integratedIn),
          type: 'extended' as 'extended',
        },
        open: initialOpen,
      };
    });

  let groups: ITreeSection<IUrvalPickerData>[] = [];

  for (let attr of attributesWithGroup) {
    const groupIndex = groups.findIndex(
      group => group.name === attr.attribute.AttributeGroup
    );
    const plainField = createPlainField(
      collectionName,
      componentName,
      modelPath,
      model
    )(attr);
    if (groupIndex === -1) {
      groups.push({
        name: attr.attribute.AttributeGroup ?? '',
        value: attr.attribute.AttributeName,
        id: attr.attribute.AttributeName + shortid.generate(),
        subList: [plainField],
        data: {
          sortOrder: getGroupSortOrder(attr.attribute.Properties),
          type: 'group' as 'group',
        },
        open: initialOpen,
      });
    } else {
      groups = adjust(groupIndex, over(fieldsLens, append(plainField)), groups);
    }
  }

  const compareFunction = getCompareFunction(sortMode);

  // Sort fields in groups
  groups.forEach(group => {
    group.subList = group.subList
      ? mergeSort(group.subList, compareFunction)
      : undefined;
  });

  let nonExtendedFields: ITreeSection<IUrvalPickerData>[] = [
    ...plainFields,
    ...groups,
    ...pageAssociationFields,
  ];

  nonExtendedFields = mergeSort(nonExtendedFields, compareFunction);

  const sortedExtendedAssociationFields = mergeSort(
    extendedAssociationFields,
    compareDisplayLabel
  );

  return nonExtendedFields.concat(sortedExtendedAssociationFields);
};

export const sublistNotAvailableItemValue = 'SublistNotAvailable';

const getSublistNotAvailableItem = (): ITreeSection<IUrvalPickerData> => ({
  id: shortid.generate(),
  value: sublistNotAvailableItemValue,
  name: sublistNotAvailableItemValue,
});

/**
 * Returns urval fields, follows all association multiple levels down
 *
 * @param levels How many levels down we should follow associations
 * @param collectionName
 * @param componentName
 * @param typeConditionObject
 * @param currentLevel
 */
export const getFieldsMultipleLevels = (
  levels: number,
  collectionName: string,
  componentName: string | null,
  typeConditionObject: ITypeTreeConditionObject | undefined,
  currentLevel: number = 0,
  modelPath: IClientAssociationModel[] = [],
  model: AvenyCollectionMap,
  sortMode: UrvalFieldSortModeType,
  initialOpen: boolean,
  attributePredicate?: AttributePredicateType,
  associationPredicate?: AssociationPredicateType,
  parent?: ITreeSection<IUrvalPickerData>
): ITreeSection<IUrvalPickerData>[] => {
  const fields = collectionName
    ? getFields(
        collectionName,
        componentName,
        model,
        sortMode,
        initialOpen,
        modelPath,
        typeConditionObject,
        attributePredicate,
        associationPredicate
      )
    : [];

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    field.parentNode = parent;
    if (field.data?.type === 'association' && field.data?.association) {
      field.isExpandable = true;
      if (currentLevel < levels) {
        field.subList = getFieldsMultipleLevels(
          levels,
          field.data.association.AssociatedCollectionName ?? '',
          field.data.association.AssociatedComponentName ?? null,
          undefined,
          currentLevel + 1,
          field.data?.modelPath &&
            append(
              field.data.association,
              field.data?.modelPath != null ? field.data?.modelPath : modelPath
            ),
          model,
          sortMode,
          initialOpen,
          attributePredicate,
          associationPredicate,
          field
        );
      } else {
        field.subList = [getSublistNotAvailableItem()];
      }
    } else if (field.data?.type === 'extended' && field.data?.association) {
      field.isExpandable = true;
      if (currentLevel < levels) {
        field.subList = getFieldsMultipleLevels(
          levels,
          field.data.association.AssociatedCollectionName ?? '',
          field.data.association.AssociatedComponentName ?? null,
          undefined,
          currentLevel + 1,
          modelPath,
          model,
          sortMode,
          initialOpen,
          attributePredicate,
          associationPredicate,
          field
        );
      } else {
        field.subList = [getSublistNotAvailableItem()];
      }
    } else if (field.data?.type === 'group') {
      field.isExpandable = true;
    }
  }

  return fields;
};
export interface IGetFieldPickerItemsProps {
  page: IUIPage;
  fieldSortMode?: UrvalFieldSortModeType;
  collectionName: string;
  componentName?: string;
  openWithAllExpandableOpen?: boolean;
  rutinId: number | undefined;
  model: AvenyCollectionMap;
}

export const getFieldPickerItems = (
  props: IGetFieldPickerItemsProps,
  levels: number
) => {
  const typeConditionInfo = getTypeConditionObject(props.page.conditionObjects);
  const typeConditionObject = typeConditionInfo
    ? typeConditionInfo.object
    : undefined;

  const sortMode = props.fieldSortMode || 'DisplayLabel';

  const predicates: IRutinFieldsPredicate | undefined = props.rutinId
    ? rutinPredicates[props.rutinId]
    : undefined;

  const selectableFields = getFieldsMultipleLevels(
    levels,
    props.collectionName,
    props.componentName || null,
    typeConditionObject,
    0,
    undefined,
    props.model,
    sortMode,
    Boolean(props.openWithAllExpandableOpen),
    predicates?.attributePredicate,
    predicates?.associationPredicate
  );

  const fieldPickerItems = createFieldPickerItems(selectableFields, props.page);

  return fieldPickerItems;
};

const fieldMatches =
  (searchValue: string) =>
  (field: ITreeSection<IUrvalPickerData>): boolean =>
    !searchValue ||
    field.name.toLowerCase().includes(searchValue.toLowerCase());

const someVisible = (items: ITreeSection<IUrvalPickerData>[]): boolean =>
  items.some(item => !item.hidden);

export const filterFields = (
  items: ITreeSection<IUrvalPickerData>[],
  searchValue: string
): ITreeSection<IUrvalPickerData>[] => {
  const newFields: ITreeSection<IUrvalPickerData>[] = [];

  for (let i = 0; i < items.length; i++) {
    const field = items[i];

    if (field.data?.type === 'field') {
      if (fieldMatches(searchValue)(field)) {
        newFields.push({
          ...field,
          hidden: !fieldMatches(searchValue)(field),
        });
      }
    } else if (field.data?.type === 'group') {
      const filteredSubFields = field.subList
        ?.filter(field => fieldMatches(searchValue)(field))
        .map<ITreeSection<IUrvalPickerData>>(field => ({
          ...field,
          hidden: !fieldMatches(searchValue)(field),
        }));

      const newField: ITreeSection<IUrvalPickerData> = {
        ...field,
        open: searchValue.length > 0 ? true : false,
        hidden: !someVisible(filteredSubFields ?? []),
        subList: filteredSubFields,
      };

      if (!newField.hidden) {
        newFields.push(newField);
      }
    } else {
      const filteredSubFields = field.subList
        ? filterFields(field.subList, searchValue)
        : undefined;

      const newField: ITreeSection<IUrvalPickerData> = {
        ...field,
        open: searchValue.length > 0 ? true : false,
        hidden: !(filteredSubFields != null && someVisible(filteredSubFields)),
        subList: filteredSubFields,
      };

      if (!newField.hidden) {
        newFields.push(newField);
      }
    }
  }

  return newFields;
};

export const filterFieldsVirtualTree = (
  items: ITreeSection<IUrvalPickerData>[],
  searchValue: string,
  openExpandable: boolean
): ITreeSection<IUrvalPickerData>[] => {
  const filteredFields: ITreeSection<IUrvalPickerData>[] = [];

  for (let i = 0; i < items.length; i++) {
    const item: ITreeSection<IUrvalPickerData> = { ...items[i] };

    if (
      (item.data?.type === 'field' && fieldMatches(searchValue)(item)) ||
      (item.value === sublistNotAvailableItemValue && !searchValue)
    ) {
      filteredFields.push(item);
    } else if (item.subList) {
      const filteredSublist = filterFieldsVirtualTree(
        item.subList,
        searchValue,
        openExpandable
      );

      if (filteredSublist.length > 0) {
        item.subList = [...filteredSublist];
        if (openExpandable) {
          item.open = Boolean(searchValue);
        }
        filteredFields.push(item);
      }
    }
  }

  return filteredFields;
};

export const setOpen = (
  items: ITreeSection<IUrvalPickerData>[],
  open: boolean,
  skipClose: boolean = false
): ITreeSection<IUrvalPickerData>[] => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.subList) {
      if (!skipClose || open) {
        item.open = open;
      }

      item.subList = setOpen(item.subList, open, skipClose).map(
        updateParent(item)
      );
    }
  }

  return [...items];
};

export const setLevel = (
  items: ITreeSection<IUrvalPickerData>[],
  currentLevel: number = 0
): ITreeSection<IUrvalPickerData>[] => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    item.level = currentLevel;
    if (item.subList) {
      item.subList = setLevel(item.subList, currentLevel + 1);
    }
  }

  return [...items];
};

export const pathMatchesFieldId =
  (field: IUrvalField | IUrvalFieldsAssociation | IUrvalExtendedField) =>
  (conditionObject: UIUrvalConditionObjectType) => {
    let path: string = '';
    if (conditionObject.type === 'Extended') {
      path = createConditionPath(
        conditionObject.association.AssociationName,
        conditionObject.modelPath
      );
    } else {
      path = createConditionPath(
        conditionObject.attribute.AttributeName,
        conditionObject.modelPath
      );
    }

    return path === field.id;
  };

export const isSelected =
  (conditionObjects: UIUrvalConditionObjectType[]) =>
  (field: IUrvalField | IUrvalFieldsAssociation | IUrvalExtendedField) =>
    any(pathMatchesFieldId(field), conditionObjects);

export const appendByAssociation = (
  condition: UIUrvalConditionObjectType,
  list: UIUrvalConditionObjectType[]
) => {
  const indexPosition = list.reduce((a, b, index) => {
    if (
      b.type !== 'Extended' &&
      ((!b.modelPath && !condition.modelPath) ||
        (b.modelPath?.length === 0 && condition.modelPath?.length === 0))
    ) {
      return index + 1;
    }
    if (
      b.modelPath &&
      b.modelPath.length > 0 &&
      b.modelPath
        .filter(x => !x.Integrated)
        .every(x =>
          condition.modelPath?.some(
            c => c.AssociationName === x.AssociationName
          )
        )
    ) {
      return index + 1;
    }
    return a;
  }, list.length);
  return insert(indexPosition, condition, list);
};

export const fieldPickerToggleOpen = (
  itemPath: Path,
  newOpen: boolean,
  items: FieldPickerItemType[]
): FieldPickerItemType[] => {
  return assocPath([...itemPath, 'open'], newOpen, items);
};

export const removeFieldPickerItem = (
  path: Path,
  items: ITreeSection<IUrvalPickerData>[]
): ITreeSection<IUrvalPickerData>[] => {
  return dissocPath(path, items);
};

export const addFieldPickerItemDuplicate = (
  addSection: ITreeSection<IUrvalPickerData>,
  items: ITreeSection<IUrvalPickerData>[],
  item?: ITreeSection<IUrvalPickerData>
): ITreeSection<IUrvalPickerData>[] => {
  const section = getSection(items, addSection);
  if (section && !item) {
    const lastDuplicate = items.reduce((result, current) => {
      if (
        current.value === section.value &&
        (!result.duplicateNr ||
          (current.duplicateNr && current.duplicateNr > result.duplicateNr))
      ) {
        result = current;
      }
      return result;
    }, section);
    if (lastDuplicate) {
      const newSection = clone(section);
      const newItem = {
        ...newSection,
        duplicateNr: (lastDuplicate.duplicateNr ?? 0) + 1,
        subList: newSection.subList?.map(clearItem),
        id: newSection.value + shortid.generate(),
      };

      newItem.subList = newItem.subList?.map(updateParent(newItem));

      items.splice(items.indexOf(lastDuplicate) + 1, 0, newItem);
    }
  }
  if (section && item) {
    const siblings = section.subList?.filter(x => x.value === item.value) ?? [];
    const itemIndex = section.subList?.indexOf(item) ?? 0;

    if (siblings && typeof itemIndex === 'number') {
      const duplicates = siblings.filter(
        sibling =>
          sibling.data?.type === 'field' && sibling.value === item.value
      );

      const lastDuplicate = last(duplicates);
      const baseItem = clone(item);
      if (
        lastDuplicate &&
        lastDuplicate.data?.type === 'field' &&
        baseItem.data?.type
      ) {
        const nextDuplicateNumber = (lastDuplicate.data?.duplicateNr || 0) + 1;
        const newItem: ITreeSection<IUrvalPickerData> = {
          ...baseItem,
          duplicateNr: nextDuplicateNumber,
          subList: baseItem.subList?.map(clearItem),
          id: baseItem.value + shortid.generate(),
          data: {
            ...baseItem.data,
            conditionObject:
              baseItem.data?.attribute &&
              (getDefaultNonExtendedConditionObject(
                baseItem.data.attribute,
                baseItem.data.modelPath,
                baseItem.data.typeTree
              ) as any as IExtendedConditionObject),
          },
        };

        newItem.subList = newItem.subList?.map(updateParent(newItem));

        section.subList?.splice(
          section.subList?.indexOf(lastDuplicate) + 1,
          0,
          newItem
        );
      }
    }
  }

  return items;
};

export const removeFieldPickerItemConditionObject = (
  path: Path,
  items: ITreeSection<IUrvalPickerData>[]
): ITreeSection<IUrvalPickerData>[] => {
  return dissocPath([...path, 'conditionObject'], items);
};

export const addFieldPickerItemConditionObject = (
  itemPath: Path,
  item: ITreeSection<IUrvalPickerData>,
  items: ITreeSection<IUrvalPickerData>[],
  collectionMap: AvenyCollectionMap
): ITreeSection<IUrvalPickerData>[] => {
  for (let i = 0; i < itemPath.length - 1; i++) {
    const pathPart = itemPath[i];

    if (typeof pathPart === 'number') {
      const currentPath = itemPath.slice(0, i + 1);

      const currentItem = path<ITreeSection<IUrvalPickerData>>(
        currentPath,
        items
      );

      if (currentItem && currentItem.data?.type === 'extended') {
        if (
          !currentItem.data?.conditionObject &&
          currentItem.data?.association
        ) {
          currentItem.data.conditionObject = getDefaultExtendedConditionObject(
            collectionMap,
            currentItem.data.association,
            currentItem.data?.modelPath
          );

          items = assocPath(currentPath, currentItem, items);
        }
      }
    }
  }
  if (!item.data?.attribute) {
    return [];
  }
  const newItem = assoc(
    'conditionObject',
    getDefaultConditionObject(
      item.data.attribute,
      item.data?.modelPath,
      item.data?.typeTree
    ),
    item
  );

  return assocPath(itemPath, newItem, items);
};

const clearItem = (
  item: ITreeSection<IUrvalPickerData>
): ITreeSection<IUrvalPickerData> => {
  if (item.data?.type === 'field') {
    return {
      ...item,
      checked: false,
      data: { ...item.data, conditionObject: undefined },
      id: item.value + shortid.generate(),
    };
  } else {
    if (item.subList) {
      item.subList = item.subList.map(clearItem);
      item.open = false;
      item.id = item.value + shortid.generate();
    }
  }

  return item;
};

const updateParent =
  (parent: ITreeSection<IUrvalPickerData>) =>
  (section: ITreeSection<IUrvalPickerData>): ITreeSection<IUrvalPickerData> => {
    return { ...section, parentNode: parent };
  };

export const addNewPage = (
  section: ITreeSection<IUrvalPickerData>,
  sections: ITreeSection<IUrvalPickerData>[]
): ITreeSection<IUrvalPickerData>[] => {
  const subList = section.subList;

  let newSubFields: Array<ITreeSection<IUrvalPickerData>> | null = null;
  if (subList) {
    const numberOfPages = subList.filter(
      subField => subField.data?.type === 'page'
    ).length;
    const firstPage = subList[0];

    if (numberOfPages > 0) {
      if (firstPage) {
        // Add another page
        const valueArr = firstPage.value.split('.');
        const value = `${valueArr
          .slice(0, valueArr.length - 1)
          .join('.')}.${shortid()}`;
        let newPage: ITreeSection<IUrvalPickerData> = {
          name: `Sida ${numberOfPages + 1}`,
          value: value,
          id: value,
          subList: firstPage.subList?.map(clearItem),
          open: false,
          parentNode: section,
          isExpandable: true,
          data: {
            ...firstPage.data,
            type: 'page',
          },
        };
        newPage.subList = newPage.subList?.map(updateParent(newPage));
        newSubFields = append(newPage, subList);
      }
    } else {
      // Convert subfields to pages
      const page1Value = `${firstPage.value}.${shortid()}`;
      const page1: ITreeSection<IUrvalPickerData> = {
        name: `Sida 1`,
        value: page1Value,
        id: page1Value,
        subList: subList,
        open: false,
        parentNode: section,
        isExpandable: true,
        data: {
          ...firstPage.data,
          type: 'page',
        },
      };
      page1.subList = page1.subList?.map(updateParent(page1));

      const page2Value = `${firstPage.value}.${shortid()}`;
      const page2: ITreeSection<IUrvalPickerData> = {
        name: `Sida 2`,
        value: page2Value,
        id: page2Value,
        subList: subList.map(clearItem),
        open: false,
        parentNode: section,
        isExpandable: true,
        data: {
          ...firstPage.data,
          type: 'page',
        },
      };
      page2.subList = page2.subList?.map(updateParent(page2));

      newSubFields = [page1, page2];
    }
  }

  if (newSubFields) {
    section.subList = newSubFields;
  }

  return sections;
};

export const removePage = (
  parent: ITreeSection<IUrvalPickerData>,
  section: ITreeSection<IUrvalPickerData>,
  sections: ITreeSection<IUrvalPickerData>[]
): ITreeSection<IUrvalPickerData>[] => {
  const path = section.value.split('.');
  const pathString = path
    .slice(0, path.length > 0 ? path.length - 1 : path.length)
    .join('.');
  const extendedItemSubFields = parent.subList?.filter(
    x => x.value.startsWith(pathString) && x.value !== section.value
  );

  // Update display labels
  if (extendedItemSubFields) {
    let newExtendedItemSubFields:
      | ITreeSection<IUrvalPickerData>[]
      | null
      | undefined;
    if (extendedItemSubFields.length > 1) {
      newExtendedItemSubFields = extendedItemSubFields.map(
        (extendedItemSubField, index) => {
          if (extendedItemSubField.data?.type === 'page') {
            return {
              ...extendedItemSubField,
              name: `Sida ${index + 1}`,
            };
          } else {
            return extendedItemSubField;
          }
        }
      );
    } else if (
      extendedItemSubFields.length === 1 &&
      extendedItemSubFields[0].data?.type === 'page'
    ) {
      newExtendedItemSubFields = extendedItemSubFields[0]?.subList;
    }

    if (newExtendedItemSubFields) {
      parent.subList = newExtendedItemSubFields;
    }
  }

  return sections;
};

export const mergeFieldPickerItems = (
  oldItems: ITreeSection<IUrvalPickerData>[],
  newItems: ITreeSection<IUrvalPickerData>[]
): ITreeSection<IUrvalPickerData>[] => {
  return oldItems.map((oldItem, i) => {
    if (
      !oldItem.subList ||
      oldItem.subList?.[0]?.value === sublistNotAvailableItemValue
    ) {
      return { ...oldItem, subList: newItems[i]?.subList };
    } else {
      const newItemSubList = newItems[i]?.subList;
      if (
        oldItem.subList &&
        oldItem.subList[0]?.value !== sublistNotAvailableItemValue &&
        newItemSubList
      ) {
        return {
          ...oldItem,
          subList: mergeFieldPickerItems(oldItem.subList, newItemSubList),
        };
      } else {
        return oldItem;
      }
    }
  });
};
